/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils\googleVoiceParsers.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


/** Parse a voice string with format: "Display Name|api-name" */
export function splitVoiceFormat(voice: string): { displayName: string; apiName: string } {
    const pipeIndex = voice.lastIndexOf('|');
    if (pipeIndex > 0) {
        return {
            displayName: voice.substring(0, pipeIndex),
            apiName: voice.substring(pipeIndex + 1),
        };
    }
    return { displayName: voice, apiName: voice };
}

/** Parse a voice name like "Spanish (Spain) - Chirp3 HD O - Female" into components */
export function parseVoiceName(voiceName: string): { language: string; accent: string; voiceType: string; gender: string } {
    const { displayName } = splitVoiceFormat(voiceName);
    const mainParts = displayName.split(' - ');

    if (mainParts.length >= 3) {
        const langAccent = mainParts[0];
        const match = langAccent.match(/^(.+?)\s*\((.+?)\)$/);
        if (match) return { language: match[1].trim(), accent: match[2].trim(), voiceType: mainParts[1], gender: mainParts[2] };
        return { language: langAccent, accent: '', voiceType: mainParts[1], gender: mainParts[2] };
    } else if (mainParts.length === 2) {
        const langAccent = mainParts[0];
        const match = langAccent.match(/^(.+?)\s*\((.+?)\)$/);
        if (match) return { language: match[1].trim(), accent: match[2].trim(), voiceType: mainParts[1], gender: '' };
    }
    return { language: displayName, accent: '', voiceType: '', gender: '' };
}

export function extractUniqueLanguages(voices: string[]): string[] {
    const languages = new Set<string>();
    voices.forEach(voice => {
        const parsed = parseVoiceName(voice);
        if (parsed.language) languages.add(parsed.language);
    });
    return Array.from(languages).sort();
}

export function extractAccentsForLanguage(voices: string[], language: string): string[] {
    const accents = new Set<string>();
    voices.forEach(voice => {
        const parsed = parseVoiceName(voice);
        if (parsed.language === language && parsed.accent) accents.add(parsed.accent);
    });
    return Array.from(accents).sort();
}

export function extractVoicesForAccent(
    voices: string[],
    language: string,
    accent: string,
): Array<{ fullName: string; apiName: string; voiceType: string; gender: string }> {
    return voices
        .map(voice => {
            const { apiName } = splitVoiceFormat(voice);
            const parsed = parseVoiceName(voice);
            return { fullName: voice, apiName, language: parsed.language, accent: parsed.accent, voiceType: parsed.voiceType, gender: parsed.gender };
        })
        .filter(v => v.language === language && v.accent === accent)
        .map(v => ({ fullName: v.fullName, apiName: v.apiName, voiceType: v.voiceType, gender: v.gender }));
}
